/* Cart complements share the theme's existing cart refresh and checkout flow. */
export function rankProducts(products, total, threshold, limit = 3) {
  const remaining = Math.max(0, threshold - total);
  const eligible = products.filter(product => product.variants?.length).map(product => ({
    ...product,
    price: Math.min(...product.variants.map(variant => variant.price))
  }));
  const compare = (a, b) => b.affinity - a.affinity || a.price - b.price || a.id - b.id;
  if (remaining === 0) return eligible.sort(compare).slice(0, limit);
  // A selected option may cost more; only its actual price may promise the goal.
  const covering = eligible.filter(product => product.variants.some(variant => variant.price >= remaining));
  covering.sort((a, b) => {
    const cost = product => Math.min(...product.variants.filter(variant => variant.price >= remaining).map(variant => variant.price));
    return cost(a) - cost(b) || compare(a, b);
  });
  const highlighted = covering[0];
  const rest = eligible.filter(product => product.id !== highlighted?.id).sort(compare);
  return (highlighted ? [highlighted, ...rest] : rest).slice(0, limit);
}

export function goalState(total, threshold, enabled) {
  return { remaining: Math.max(0, threshold - total), reached: enabled && threshold > 0 && total >= threshold };
}

export function formatMoney(cents) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(cents / 100);
}

export function boot(doc = document, request = fetch) {
  const mounted = new WeakSet();
  const snapshots = new WeakMap();
  const viewed = new Set();
  const intersecting = new WeakSet();
  let viewObserver;
  const successes = new Set();
  let busy = false;
  let pendingGoal = false;
  let currentTotal;
  let operation = 0;

  const emit = (event, details) => {
    const view = doc.defaultView;
    view.dataLayer = view.dataLayer || [];
    view.dataLayer.push({ event, currency: 'MXN', ...details });
  };
  const element = (tag, className, text) => {
    const node = doc.createElement(tag);
    node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const status = (root, message, error = false) => {
    const target = root.querySelector('[data-upsell-status]');
    target.textContent = message;
    target.classList.toggle('cart-upsell__status--error', error);
  };
  const isVisible = root => {
    const drawer = root.closest('.mini-cart');
    return root.isConnected && !root.hidden && (!drawer || drawer.getAttribute('aria-hidden') === 'false');
  };
  const observeViews = () => {
    doc.querySelectorAll('[data-cart-upsell]').forEach(root => {
      const snapshot = snapshots.get(root);
      if (!snapshot || !isVisible(root) || (viewObserver && !intersecting.has(root))) return;
      const key = `${snapshot.surface}:${snapshot.fingerprint}`;
      if (viewed.has(key)) return;
      viewed.add(key);
      emit('covalt_cart_upsell_view', { surface: snapshot.surface, cart_total: snapshot.total / 100, product_ids: snapshot.selected.map(product => product.id) });
    });
  };
  const setBusy = () => {
    doc.querySelectorAll('.cart-upsell__variant').forEach(select => { select.disabled = busy; });
    doc.querySelectorAll('[data-upsell-add]').forEach(button => {
      button.disabled = busy || !button.dataset.variantId;
      button.setAttribute('aria-busy', busy ? 'true' : 'false');
    });
  };
  const mount = root => {
    if (mounted.has(root)) return;
    let snapshot;
    try { snapshot = JSON.parse(root.querySelector('[data-cart-upsell-data]').textContent); }
    catch { return; }
    const shipping = snapshot.showShipping && snapshot.threshold > 0;
    const state = goalState(snapshot.total, snapshot.threshold, shipping);
    const selected = rankProducts(snapshot.products, snapshot.total, shipping ? snapshot.threshold : 0);
    if (!selected.length) return;
    snapshots.set(root, { ...snapshot, selected });
    mounted.add(root);
    root.querySelector('[data-upsell-heading]').textContent = shipping && !state.reached ? snapshot.heading : snapshot.completeHeading;
    const progressText = root.querySelector('[data-upsell-progress-text]');
    progressText.textContent = state.reached ? '¡Tu compra ya alcanza el envío gratis!' : `Te faltan ${formatMoney(state.remaining)} MXN para el envío gratis.`;
    progressText.hidden = !shipping;
    const progress = root.querySelector('[data-upsell-progress]');
    progress.max = snapshot.threshold || 1;
    progress.value = Math.min(snapshot.total, snapshot.threshold);
    progress.hidden = !shipping;
    const list = root.querySelector('[data-upsell-products]');
    selected.forEach((product, index) => {
      const card = element('article', 'cart-upsell__product');
      const link = element('a', 'cart-upsell__image-link');
      link.href = product.url;
      link.setAttribute('tabindex', '-1');
      link.setAttribute('aria-hidden', 'true');
      if (product.image) {
        const image = element('img', 'cart-upsell__image');
        image.src = product.image;
        image.alt = '';
        image.width = 72;
        image.height = 72;
        image.loading = 'lazy';
        link.append(image);
      }
      const body = element('div', 'cart-upsell__product-body');
      const title = element('a', 'cart-upsell__title', product.title);
      title.href = product.url;
      const price = element('p', 'cart-upsell__price');
      const hint = element('p', 'cart-upsell__hint');
      const button = element('button', 'cart-upsell__add button button--primary', snapshot.addLabel);
      button.type = 'button';
      button.dataset.upsellAdd = '';
      button.dataset.productId = product.id;
      button.setAttribute('aria-label', `${snapshot.addLabel} ${product.title}`);
      const update = variant => {
        price.textContent = variant ? variant.formattedPrice : `Desde ${formatMoney(product.price)}`;
        button.dataset.variantId = variant ? variant.id : '';
        hint.textContent = '';
        if (shipping && state.remaining > 0 && variant) {
          const missing = Math.max(0, state.remaining - variant.price);
          hint.textContent = missing === 0 ? 'Con este complemento alcanzas el envío gratis*' : `Te acercas: faltarían ${formatMoney(missing)} MXN*`;
        } else if (shipping && state.remaining > 0 && index === 0 && product.variants.some(variant => variant.price >= state.remaining)) {
          hint.textContent = 'Elige una presentación para ver tu avance.';
        }
        hint.hidden = !hint.textContent;
        setBusy();
      };
      body.append(title, price);
      if (product.requiresSelection) {
        const select = element('select', 'cart-upsell__variant');
        select.setAttribute('aria-label', `Presentación de ${product.title}`);
        const placeholder = element('option', '', 'Elige una presentación');
        placeholder.value = '';
        select.append(placeholder);
        product.variants.forEach(variant => {
          const option = element('option', '', `${variant.title} — ${variant.formattedPrice}`);
          option.value = variant.id;
          select.append(option);
        });
        select.addEventListener('change', () => update(product.variants.find(variant => String(variant.id) === select.value)));
        body.append(select);
        update(null);
      } else update(product.variants[0]);
      body.append(hint, button);
      card.append(link, body);
      list.append(card);
    });
    if (shipping && !state.reached) list.append(element('p', 'cart-upsell__note', '*El total se confirma al agregar; los descuentos pueden cambiarlo.'));
    root.hidden = false;
    if (viewObserver) viewObserver.observe(root);
    setBusy();
    if (pendingGoal && shipping && currentTotal < snapshot.threshold && state.reached) {
      pendingGoal = false;
      emit('covalt_cart_upsell_threshold_reached', { surface: snapshot.surface, cart_total: snapshot.total / 100, threshold: snapshot.threshold / 100 });
    }
    currentTotal = snapshot.total;
  };
  const refresh = () => {
    doc.querySelectorAll('[data-cart-upsell]').forEach(mount);
    observeViews();
  };
  const onAdd = async event => {
    const button = event.target.closest('[data-upsell-add]');
    if (!button || busy || !button.dataset.variantId) return;
    const root = button.closest('[data-cart-upsell]');
    const snapshot = snapshots.get(root);
    if (!snapshot) return;
    const variantId = Number(button.dataset.variantId);
    const productId = Number(button.dataset.productId);
    event.preventDefault();
    busy = true;
    const id = ++operation;
    setBusy();
    status(root, 'Agregando…');
    try {
      const response = await request(`${doc.defaultView.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      let result;
      try { result = await response.json(); } catch { result = {}; }
      if (!response.ok) throw new Error(response.status === 422 ? 'Esta presentación ya no está disponible. Elige otra o vuelve a intentar.' : 'No pudimos agregar el producto. Vuelve a intentar.');
      const key = String(id);
      if (!successes.has(key)) {
        successes.add(key);
        emit('covalt_cart_upsell_add', { surface: snapshot.surface, product_id: productId, variant_id: variantId, quantity: 1, cart_total_before: snapshot.total / 100 });
      }
      pendingGoal = snapshot.showShipping && snapshot.total < snapshot.threshold;
      currentTotal = snapshot.total;
      status(root, 'Producto agregado. Actualizando carrito…');
      let finishRefresh;
      const onRendered = () => finishRefresh(true);
      const refreshDone = new Promise(resolve => {
        const timeout = doc.defaultView.setTimeout(() => finishRefresh(false), 5000);
        finishRefresh = rendered => {
          doc.defaultView.clearTimeout(timeout);
          doc.removeEventListener('cart:rerendered', onRendered, true);
          resolve(rendered);
        };
        doc.addEventListener('cart:rerendered', onRendered, true);
      });
      doc.documentElement.dispatchEvent(new doc.defaultView.CustomEvent('product:added', { bubbles: true, detail: { quantity: 1, variant: result.items?.[0] } }));
      // Read authoritative totals even if the last eligible complement vanished.
      const cartRead = request(`${doc.defaultView.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } })
        .then(response => response.ok ? response.json() : null).catch(() => null);
      const [cart, rendered] = await Promise.all([cartRead, refreshDone]);
      if (cart?.currency === 'MXN' && Number.isFinite(cart.total_price)) {
        if (pendingGoal && cart.total_price >= snapshot.threshold) {
          pendingGoal = false;
          emit('covalt_cart_upsell_threshold_reached', { surface: snapshot.surface, cart_total: cart.total_price / 100, threshold: snapshot.threshold / 100 });
        }
        currentTotal = cart.total_price;
      }
      if (!rendered && root.isConnected) {
        // The POST succeeded: do not offer a second add from stale cards.
        root.hidden = true;
        doc.documentElement.dispatchEvent(new doc.defaultView.CustomEvent('cart:refresh', { bubbles: true, detail: { scrollToTop: false } }));
      }
    } catch (error) {
      status(root, error instanceof TypeError ? 'No pudimos conectar. Revisa tu conexión y vuelve a intentar.' : error.message, true);
    } finally {
      busy = false;
      setBusy();
    }
  };
  doc.addEventListener('click', onAdd);
  doc.addEventListener('cart:rerendered', refresh, true);
  doc.addEventListener('shopify:section:load', refresh);
  doc.defaultView.addEventListener('pageshow', refresh);
  if (doc.defaultView.IntersectionObserver) {
    viewObserver = new doc.defaultView.IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) intersecting.add(entry.target);
        else intersecting.delete(entry.target);
      });
      observeViews();
    }, { threshold: 0.1 });
  }
  const observer = new doc.defaultView.MutationObserver(() => { refresh(); });
  const header = doc.querySelector('[data-section-type="header"]');
  if (header) observer.observe(header, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-hidden'] });
  refresh();
  return { refresh, destroy() { observer.disconnect(); viewObserver?.disconnect(); doc.removeEventListener('click', onAdd); doc.removeEventListener('cart:rerendered', refresh, true); doc.removeEventListener('shopify:section:load', refresh); doc.defaultView.removeEventListener('pageshow', refresh); } };
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
}
